/*
 * Lightweight storefront extras (no dependencies, no app):
 *  - Favourites (wishlist): heart buttons + count in the header, list on the
 *    favourites page. Stored in localStorage on this device.
 *  - Recently viewed products: recorded on product pages, rendered by the
 *    recently-viewed section.
 * Products are looked up via the public /products/<handle>.js endpoint.
 */
(() => {
  const WISHLIST_KEY = 'padel:wishlist';
  const RECENT_KEY = 'padel:recent';
  const RECENT_MAX = 12;

  const read = (key) => {
    try {
      const value = JSON.parse(localStorage.getItem(key));
      return Array.isArray(value) ? value : [];
    } catch (error) {
      return [];
    }
  };

  const write = (key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      /* storage blocked: features degrade silently */
    }
  };

  /* ---------- wishlist ---------- */

  const syncWishlist = () => {
    const saved = read(WISHLIST_KEY);
    document.querySelectorAll('[data-wishlist-handle]').forEach((button) => {
      const on = saved.includes(button.dataset.wishlistHandle);
      button.setAttribute('aria-pressed', String(on));
      button.setAttribute('aria-label', on ? button.dataset.labelRemove : button.dataset.labelAdd);
    });
    document.querySelectorAll('[data-wishlist-count]').forEach((badge) => {
      badge.textContent = saved.length ? String(saved.length) : '';
      badge.hidden = saved.length === 0;
    });
  };

  const toggleWishlist = (handle) => {
    const saved = read(WISHLIST_KEY);
    const next = saved.includes(handle) ? saved.filter((h) => h !== handle) : [...saved, handle];
    write(WISHLIST_KEY, next);
    syncWishlist();
    renderWishlistPage();
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-wishlist-handle]');
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    toggleWishlist(button.dataset.wishlistHandle);
  });

  /* ---------- product cards ---------- */

  const money = (cents) => {
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency }).format(cents / 100);
    } catch (error) {
      return (cents / 100).toFixed(2);
    }
  };

  const fetchProduct = (handle) =>
    fetch(`/products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);

  const buildCard = (product, withWishlist) => {
    const item = document.createElement('li');
    item.className = 'mini-card';

    const link = document.createElement('a');
    link.className = 'mini-card__link';
    link.href = product.url;
    const hidden = document.createElement('span');
    hidden.className = 'visually-hidden';
    hidden.textContent = product.title;
    link.appendChild(hidden);
    item.appendChild(link);

    const media = document.createElement('div');
    media.className = 'mini-card__media';
    if (product.featured_image) {
      const img = document.createElement('img');
      img.className = 'mini-card__img';
      const sep = product.featured_image.includes('?') ? '&' : '?';
      img.src = `${product.featured_image}${sep}width=500`;
      img.alt = product.title;
      img.loading = 'lazy';
      media.appendChild(img);
    }
    item.appendChild(media);

    const info = document.createElement('div');
    info.className = 'mini-card__info';
    const title = document.createElement('span');
    title.className = 'mini-card__title';
    title.textContent = product.title;
    const price = document.createElement('span');
    price.className = 'mini-card__price';
    price.textContent = money(product.price);
    if (product.compare_at_price > product.price) {
      const compare = document.createElement('s');
      compare.className = 'mini-card__compare';
      compare.textContent = money(product.compare_at_price);
      price.appendChild(compare);
    }
    info.append(title, price);
    item.appendChild(info);

    if (withWishlist) {
      const template = document.getElementById('wishlist-button-template');
      if (template) {
        const button = template.content.firstElementChild.cloneNode(true);
        button.dataset.wishlistHandle = product.handle;
        media.appendChild(button);
      }
    }
    return item;
  };

  const renderInto = async (container, handles, withWishlist) => {
    const products = (await Promise.all(handles.map(fetchProduct))).filter(Boolean);
    const list = container.querySelector('[data-cards]');
    list.replaceChildren(...products.map((product) => buildCard(product, withWishlist)));
    syncWishlist();
    return products.length;
  };

  /* ---------- favourites page ---------- */

  function renderWishlistPage() {
    const container = document.querySelector('[data-wishlist-page]');
    if (!container) return;
    const handles = read(WISHLIST_KEY);
    const empty = container.querySelector('[data-empty]');
    const list = container.querySelector('[data-cards]');
    if (!handles.length) {
      list.replaceChildren();
      empty.hidden = false;
      return;
    }
    empty.hidden = true;
    renderInto(container, handles, true).then((count) => {
      empty.hidden = count > 0;
    });
  }

  /* ---------- recently viewed ---------- */

  const renderRecent = async () => {
    const container = document.querySelector('[data-recently-viewed]');
    if (!container) return;
    const current = container.dataset.currentHandle;
    let recent = read(RECENT_KEY);
    if (current) {
      recent = [current, ...recent.filter((h) => h !== current)].slice(0, RECENT_MAX);
      write(RECENT_KEY, recent);
    }
    const handles = recent.filter((h) => h !== current).slice(0, Number(container.dataset.max || 6));
    if (!handles.length) return;
    const count = await renderInto(container, handles, false);
    container.hidden = count === 0;
  };

  /* ---------- init ---------- */

  const init = () => {
    syncWishlist();
    renderWishlistPage();
    renderRecent();
    // cards re-render after filtering/sorting/pagination: keep hearts in sync
    let queued = false;
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        syncWishlist();
      });
    }).observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
